import {Box, Button, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useState} from 'react'
import {Face} from 'src/common/models/facial-recognition/FaceDTO'
import FadeBox from 'src/components/FadeBox'
import {Input} from 'src/components/input/Input'
import InfiniteScrollLoading from 'src/components/loading/InfiniteScrollLoading'
import ModalLateral, {ModalLateralFooter} from 'src/components/modal/ModalLateral'
import SlideTypography from 'src/components/SlideTypography'
import useFaceBinding from 'src/hooks/api/facial/useFaceBinding'
import useFaces from 'src/hooks/api/facial/useFaces'
import useDebounce from 'src/hooks/useDebounce'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import arrowIcon from 'src/images/arrowIcon.svg'
import SearchIconInputSmallChangeColor from 'src/images/SearchIconInputSmall'
import zoomInIcon from 'src/images/zoomInIcon.svg'
import zoomOutIcon from 'src/images/zoomOutIcon.svg'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import {FaceDetection} from 'src/services/api'
import createStyleSheet from 'src/utilities/createStyleSheet'
import {useWindowSize} from 'usehooks-ts'
import {TranslateGender} from '../../../utilities/translateGender'
import {FaceBindingFrame} from './FaceBindingFrame'
import {HistoryPhotoFrameSkeletonMap} from './HistoryPhotoFrameSkeleton'
import PhotoCard from './PhotoCard'

interface Props {
  faceDetection: FaceDetection
  cameraName?: string
  companyName?: string
  closeModal: () => void
  onReturn: () => void
}

function LabelText({label, text, to}: {label: string; text: string | number; to?: string}) {
  const {styledTheme} = usePersonalizedThemeByCompany()

  return (
    <Box display='flex' columnGap='0.5ch'>
      <Typography variant='label' weight={700} size='16px' color={styledTheme.faceRecognition.titleColor}>
        {label}:
      </Typography>
      <Typography variant='label' weight={400} size='16px' textDecoration={to ? 'line-through' : 'none'}>
        {text}
      </Typography>
      {to && (
        <Box display='flex'>
          <img src={arrowIcon} alt='Arrow' style={{transform: 'rotate(270deg)', margin: '0 8px', width: '20px'}} />
          <Typography variant='label' weight={700} size='16px' color={styledTheme.faceRecognition.titleColor}>
            {to}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default function FaceBindingModal({faceDetection, cameraName, companyName, closeModal, onReturn}: Props) {
  const {selectedCompanies} = useCompaniesSelector()

  const {enqueueSnackbar} = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)

  const {data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage} = useFaces({
    search: {faceName: debouncedSearch.length ? debouncedSearch : undefined},
    filter: {companyIds: selectedCompanies.map((it) => it.id)},
    includes: ['faceImages;'],
  })
  const {isPending, mutateAsync} = useFaceBinding()
  const windowSize = useWindowSize()
  const isMobile = windowSize.width <= 460

  const [faceFocused, setFaceFocused] = useState<Face>()
  const [isInDetailedMode, setIsInDetailedMode] = useState(false)
  const [isHoveringPhoto, setIsHoveringPhoto] = useState(false)

  const isPersonUnknown = !faceDetection?.face || !faceDetection?.face.name

  const bindFace = async () => {
    if (!faceFocused) return
    try {
      await mutateAsync({bind_at_face_id: faceFocused.id, face_crop_image_key: faceDetection.cropImageKey})
      enqueueSnackbar(`Face vinculada ao rosto ${faceFocused.name} com sucesso`, {variant: 'success'})
      closeModal()
    } catch (error) {
      enqueueSnackbar(`Falha ao vincular face ${faceFocused.name} ao rosto detectado, tente novamente mais tarde`, {variant: 'error'})
    }
  }

  const handleUnfocusPhoto = () => {
    setIsInDetailedMode(false)
    setIsHoveringPhoto(false)
  }

  const onMouseEnter = () => setIsHoveringPhoto(true)
  const onMouseLeave = () => setIsHoveringPhoto(false)

  const onCloseModal = () => {
    setIsHoveringPhoto(false)
    setIsInDetailedMode(false)
    closeModal()
  }
  const {styledTheme} = usePersonalizedThemeByCompany()

  return (
    <ModalLateral isOpen={!!faceDetection} closeModal={onCloseModal} title='Vincular a face existente' onReturn={onReturn}>
      <Box display='flex' direction='column' rowGap='24px' style={styles.overflow} maxHeight='84vh' flex={1}>
        {isInDetailedMode && (
          <Box>
            <PhotoCard
              width='100%'
              height='390px'
              src={`${process.env.REACT_APP_EVENT_IMAGES_BASE_URL}/${faceDetection.imageKey}`}
              style={{cursor: 'pointer', aspectRatio: '16/9'}}
              onClick={handleUnfocusPhoto}>
              <Box position='relative' width='100%' display='flex' justify='flex-end'>
                <img src={zoomOutIcon} alt='close' width={30} height={26} style={{margin: 8}} color='#433942' />
              </Box>
            </PhotoCard>

            <Box display='flex' rowGap='4px' direction='row' justifyContent='space-between' marginTop='8px'>
              <Box maxWidth={'140px'}>
                <SlideTypography variant='label' size='14px'>
                  {cameraName || 'DESCONHECIDO'}
                </SlideTypography>
              </Box>
            </Box>
          </Box>
        )}

        <Box display='flex' flex={1} direction={isMobile && !isPersonUnknown ? 'column' : 'row'} rowGap='24px'>
          <Box
            display='flex'
            direction='column'
            width={isMobile && !isPersonUnknown ? 'auto' : 'fit-content'}
            rowGap='8px'
            marginRight={isPersonUnknown && isInDetailedMode ? '0' : '12px'}>
            <Box display='flex' width='100%' direction='column' rowGap='12px'>
              <Box display='flex' width='100%' justifyContent={'space-between'} columnGap='16px'>
                {!isInDetailedMode && (
                  <PhotoCard
                    src={`${process.env.REACT_APP_CROPPED_IMAGES_BASE_URL}/${faceDetection.cropImageKey}`}
                    width={isMobile && !isPersonUnknown ? '165px' : '133px'}
                    height={isMobile && !isPersonUnknown ? '200px' : '153px'}
                    onClick={() => setIsInDetailedMode(true)}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}>
                    {!isHoveringPhoto && (
                      <Box position='absolute' width={isMobile && !isPersonUnknown ? '165px' : '133px'} display='flex' justify='flex-end'>
                        <img src={zoomInIcon} alt='close' width={20} height={16} style={{margin: 8}} color='#433942' />
                      </Box>
                    )}

                    <FadeBox isUnmounting={!isHoveringPhoto} onFadeOutComplete={onMouseLeave}>
                      <Box width='100%' display='flex' justify='center' align='center' height='100%'>
                        <img src={zoomInIcon} alt='close' width={52} height={42} color='#433942' />
                      </Box>
                    </FadeBox>
                  </PhotoCard>
                )}
              </Box>
            </Box>

            {!isInDetailedMode && (
              <Box display='flex' rowGap='4px' direction='column'>
                <Box maxWidth={isInDetailedMode || isPersonUnknown ? '140px' : '282px'}>
                  <SlideTypography variant='label' size='14px'>
                    {cameraName || 'DESCONHECIDO'}
                  </SlideTypography>
                </Box>
              </Box>
            )}
          </Box>

          <Box
            display='flex'
            flex={1}
            direction='column'
            rowGap='20px'
            align='center'
            height='fit-content'
            padding={isMobile && !isPersonUnknown ? '0 4px' : '9'}>
            <Box flex={1} width='99.8%' display='flex' border='1px solid #0F4C81' borderRadius='8px' direction='column'>
              <Box display='flex' direction='column' height='fit-content' padding='16px' rowGap='14px'>
                <LabelText label='Nome' text={faceDetection.face?.name ?? 'Desconhecido'} to={faceFocused?.name} />
                {faceDetection.age && <LabelText label='Idade' text={faceDetection.age} />}
                {faceDetection.gender && <LabelText label='Gênero' text={TranslateGender(faceDetection.gender)} />}
                <LabelText label='Empresa' text={companyName ?? '-'} />
              </Box>
            </Box>
          </Box>
        </Box>

        <Box display='flex' direction='column' rowGap='8px'>
          <Box display='flex' justifyContent='space-between'>
            <Box display='flex' direction='column'>
              <Typography variant='h3' size='16px' weight={700} lineHeight='16px'>
                Vincular a face
              </Typography>
              <Typography variant='h5' size='12px' weight={500}>
                Escolha uma face já cadastrada para vincular ao rosto detectado
              </Typography>
            </Box>
            <Input.Root
              hasIcon
              controlled
              maxLength={50}
              height='34px'
              maxWidth='200px'
              minWidth='150px'
              width='100%'
              leftIcon={<SearchIconInputSmallChangeColor color={styledTheme.icons.colors.darkContour} />}
              labelFontWeight='400'
              placeholder='Pesquisar face'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box
            maxHeight={'500px'}
            padding='6px'
            style={styles.grid}
            onScroll={(e) => {
              const target = e.target as HTMLElement
              const GAP = 10
              const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= GAP
              if (nearBottom && hasNextPage) fetchNextPage()
            }}>
            {isLoading ? (
              <HistoryPhotoFrameSkeletonMap />
            ) : (
              data?.pages
                .flatMap((page) => page.data.data.entities)
                .map((item, index) => (
                  <FaceBindingFrame key={index} face={item} onClick={(face) => setFaceFocused(face)} isSelected={item.id === faceFocused?.id} />
                ))
            )}
            <InfiniteScrollLoading isLoading={isFetchingNextPage} bottom={90} />
          </Box>
        </Box>

        <ModalLateralFooter>
          <Box display='flex' flex={1} justifyContent='flex-end' padding='0 40px' columnGap='12px'>
            <Button width='150px' height='48px' variant='outlined' onClick={onReturn}>
              Cancelar
            </Button>
            <Button
              width='150px'
              height='48px'
              variant='contained'
              onClick={bindFace}
              isLoading={isPending}
              disabledTextColor='#B9B9B9'
              disabled={!faceFocused || isLoading}>
              Salvar
            </Button>
          </Box>
        </ModalLateralFooter>
      </Box>
    </ModalLateral>
  )
}

const styles = createStyleSheet({
  overflow: {
    paddingRight: 6,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    '-ms-overflow-style': 'none',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
    overflow: 'auto',
    rowGap: '12px',
    columnGap: '16px',
    scrollbarWidth: 'thin',
    '-ms-overflow-style': 'none',
  },
})
